import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../../home/hooks/useInView';

export function Contact() {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6mfalo5dsf7sr1n6ac0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-block px-4 py-2 bg-[#2c5282]/10 rounded-full text-sm font-semibold text-[#2c5282] mb-4"
          >
            聯絡我們
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-6"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            預約初步諮詢
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            歡迎與我們聯繫，討論您的智產權需求。我們會盡快回覆您的訊息
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="ip-consult-form">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#1a365d] mb-2">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c5282] focus:border-transparent transition-all"
                  placeholder="請輸入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#1a365d] mb-2">
                  電子郵件 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c5282] focus:border-transparent transition-all"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#1a365d] mb-2">
                  聯絡電話
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c5282] focus:border-transparent transition-all"
                  placeholder="0912-345-678"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#1a365d] mb-2">
                  諮詢內容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c5282] focus:border-transparent transition-all resize-none"
                  placeholder="請簡述您的需求或想詢問的問題（限 500 字）"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length} / 500
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-8 py-4 bg-[#2c5282] text-white rounded-lg font-semibold hover:bg-[#1a365d] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>送出中...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    <span>送出諮詢</span>
                  </>
                )}
              </motion.button>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"
                >
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  感謝您的來信！我們會盡快與您聯繫。
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                >
                  <i className="ri-error-warning-line mr-2"></i>
                  送出失敗，請稍後再試或直接來電聯繫。
                </motion.div>
              )}
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            <div className="bg-gradient-to-br from-[#2c5282] to-[#1a365d] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                聯絡資訊
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">電子郵件</div>
                    <a href="mailto:complus@ms21.hinet.net" className="text-lg font-medium hover:underline cursor-pointer">
                      complus@ms21.hinet.net
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">聯絡電話</div>
                    <a href="tel:0227361182" className="text-lg font-medium hover:underline cursor-pointer">
                      (02) 2736-1182
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">地址</div>
                    <div className="text-lg font-medium leading-snug">
                      台北市信義區基隆路二段189號3樓之5
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-sm text-white/70 mb-1">服務時間</div>
                    <div className="text-lg font-medium">週一至週五 9:00 - 18:00</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#f8f9fa] to-white rounded-2xl p-8 border border-gray-200">
              <h3 className="text-xl font-bold text-[#1a365d] mb-4" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                初次諮詢流程
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#2c5282] text-white rounded-full text-sm font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <div className="font-semibold text-[#1a365d] mb-1">填寫諮詢表單</div>
                    <div className="text-sm text-gray-600">簡述您的需求與問題</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#2c5282] text-white rounded-full text-sm font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <div className="font-semibold text-[#1a365d] mb-1">專人聯繫</div>
                    <div className="text-sm text-gray-600">我們會在 1-2 個工作天內回覆</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#2c5282] text-white rounded-full text-sm font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <div className="font-semibold text-[#1a365d] mb-1">預約諮詢</div>
                    <div className="text-sm text-gray-600">安排時間進行詳細討論</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}